import Autoplay from 'embla-carousel-autoplay';
import { ArrowLeft, DollarSign, Edit, ExternalLink, History, Image as ImageIcon, Info, Package, Percent, TrendingUp, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { AssetTemplate } from '@/types/assetTemplate';
import AssetInstancesTab from '../components/AssetInstancesTab';
import AssetPerformanceTab from '../components/AssetPerformanceTab';
import AssetRevenueTab from '../components/AssetRevenueTab';
import AssetTemplateForm from '../components/AssetTemplateForm';
import Header from '../components/Header';
import ReorderableGrid from '../components/ReorderableGrid';
import { AspectRatioImage } from '../components/ui/aspect-ratio-image';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatProductDimensions, parseProductDimensions } from '../lib/productDimensions';
import { getSupplierStock } from '../utils/supplierStock';
import RentalHistory from './RentalHistory';

/** Milestone months for summary table (matches Break-Even Analysis key durations, including 36). */
const BREAK_EVEN_SUMMARY_MO = [1, 2, 3, 6, 12, 24, 36];

const BreakEvenSummaryCard: React.FC<{ templateId: string; supplierId: string }> = ({ templateId, supplierId }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`/api/break-even/template/${templateId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.data) setData(d.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [templateId]);

  const rows = useMemo(() => {
    const table = data?.pricing_table as any[] | undefined;
    if (!Array.isArray(table)) return [];
    return table
      .filter(p => BREAK_EVEN_SUMMARY_MO.includes(p.duration_months))
      .sort((a, b) => a.duration_months - b.duration_months);
  }, [data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Break-Even Summary</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : rows.length > 0 ? (
          <>
            <table className="w-full text-sm mb-3">
              <thead><tr className="border-b"><th className="text-left py-2">Duration</th><th className="text-right py-2">Monthly</th><th className="text-right py-2">Break-even</th><th className="text-right py-2">Status</th></tr></thead>
              <tbody>
                {rows.map((p: any) => (
                  <tr key={p.duration_months} className="border-b last:border-0">
                    <td className="py-1.5">{p.duration_months} month{p.duration_months > 1 ? 's' : ''}</td>
                    <td className="py-1.5 text-right font-mono">£{p.monthly_price.toFixed(2)}</td>
                    <td className="py-1.5 text-right">{p.break_even_months > 0 ? `${p.break_even_months} months` : '—'}</td>
                    <td className="py-1.5 text-right">
                      {p.breaks_even_in_term
                        ? <span className="text-green-600 font-medium">Breaks even ✓</span>
                        : <span className="text-muted-foreground">Needs {p.break_even_months > 0 ? Math.ceil(p.break_even_months / p.duration_months) : '—'} rentals</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <a href={`/break-even?product=${templateId}`} className="text-sm text-primary hover:underline">
              View Full Break-Even Analysis →
            </a>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            No margin tiers configured.{' '}
            <a href={`/suppliers/${supplierId}`} className="text-primary hover:underline">Configure margins</a> for this supplier.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

interface TemplatePricingOutcome {
  outcome: string;
  skip_reason?: string | null;
  error_message?: string | null;
}

interface TemplatePricingTierSnap {
  duration_count: number;
  duration_unit: string;
  total_price: number;
}

interface TemplatePricingEventRow {
  id: string;
  status: string;
  created_at: string;
  template_outcome: TemplatePricingOutcome | null;
  rental_pricing_before?: TemplatePricingTierSnap[] | null;
  rental_pricing_after?: TemplatePricingTierSnap[] | null;
}

const calendarTotalAtMonths = (rows: TemplatePricingTierSnap[] | null | undefined, mo: number) => {
  if (!rows?.length) return null;
  const h = rows.find(r => r.duration_unit === 'months' && r.duration_count === mo);
  return h != null ? h.total_price : null;
};

const CALENDAR_PLANNER_MONTHS = 36;

const fmtGbp = (v: number) => {
  const neg = v < 0;
  const s = Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return neg ? `-£${s}` : `£${s}`;
};

const normCalendarMonthUnit = (u: string) => u.replace(/s$/i, '').toLowerCase();

const buildCalendarTierByMonthMap = (tiers: unknown[]) => {
  const m = new Map<number, any>();
  if (!Array.isArray(tiers)) return m;
  for (const t of tiers as any[]) {
    if (t?.pricing_type !== 'calendar') continue;
    if (normCalendarMonthUnit(String(t.duration_unit ?? '')) !== 'month') continue;
    const n = Math.round(Number(t.duration_count));
    if (!Number.isFinite(n) || n < 1 || n > CALENDAR_PLANNER_MONTHS) continue;
    m.set(n, t);
  }
  return m;
};

const buildPublishCompareRows = (ev: TemplatePricingEventRow) => {
  const out: { mo: number; oldP: number | null; newP: number | null; diff: number | null }[] = [];
  for (let mo = 1; mo <= CALENDAR_PLANNER_MONTHS; mo++) {
    const oldP = calendarTotalAtMonths(ev.rental_pricing_before, mo);
    const newP = calendarTotalAtMonths(ev.rental_pricing_after, mo);
    if (oldP == null && newP == null) continue;
    const diff =
      oldP != null && newP != null ? Number(newP) - Number(oldP) : null;
    out.push({ mo, oldP, newP, diff });
  }
  return out;
};

const TemplatePricingPublishHistoryCard: React.FC<{ templateId: string }> = ({ templateId }) => {
  const [rows, setRows] = useState<TemplatePricingEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<TemplatePricingEventRow | null>(null);

  const compareRows = useMemo(
    () => (selected ? buildPublishCompareRows(selected) : []),
    [selected],
  );

  useEffect(() => {
    const t = localStorage.getItem('token');
    fetch(`/api/asset-templates/${templateId}/pricing-events?limit=12`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (d?.success && Array.isArray(d.data)) setRows(d.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [templateId]);

  const fmtOutcome = (o: TemplatePricingOutcome | null) => {
    if (!o) return '—';
    if (o.outcome === 'recalculated') return 'Recalculated';
    if (o.outcome === 'skipped') return `Skipped${o.skip_reason ? `: ${o.skip_reason}` : ''}`;
    if (o.outcome === 'failed') return o.error_message || 'Failed';
    return o.outcome;
  };

  const openDetail = (r: TemplatePricingEventRow) => {
    setSelected(r);
    setDetailOpen(true);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Price history</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No publish history for this product yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 pr-3">When</th>
                  <th className="text-left py-2 pr-3">Job</th>
                  <th className="text-left py-2 pr-3">Outcome</th>
                  <th className="text-right py-2">12 mo rent</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const b = calendarTotalAtMonths(r.rental_pricing_before, 12);
                  const a = calendarTotalAtMonths(r.rental_pricing_after, 12);
                  const rentLbl = b != null || a != null
                    ? `${b != null ? `£${Number(b).toFixed(2)}` : '—'} → ${a != null ? `£${Number(a).toFixed(2)}` : '—'}`
                    : '—';
                  return (
                    <tr
                      key={r.id}
                      role="button"
                      tabIndex={0}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none"
                      onClick={() => openDetail(r)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openDetail(r);
                        }
                      }}
                    >
                      <td className="py-1.5 pr-3 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="py-1.5 pr-3"><Badge variant="secondary">{r.status}</Badge></td>
                      <td className="py-1.5 pr-3 text-muted-foreground">{fmtOutcome(r.template_outcome)}</td>
                      <td className="py-1.5 text-right font-mono text-xs tabular-nums">{rentLbl}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Dialog
          open={detailOpen}
          onOpenChange={open => {
            setDetailOpen(open);
            if (!open) setSelected(null);
          }}
        >
          <DialogContent className="!flex !h-auto max-h-[85vh] w-[calc(100vw-2rem)] max-w-2xl flex-col gap-0 overflow-y-auto overflow-x-hidden p-0">
            <DialogHeader className="relative shrink-0 space-y-2 border-b px-6 pb-4 pt-5 pr-14 text-left">
              <DialogTitle>Calendar prices</DialogTitle>
              {selected && (
                <DialogDescription>
                  {new Date(selected.created_at).toLocaleString()} · {fmtOutcome(selected.template_outcome)}
                </DialogDescription>
              )}
              <DialogClose asChild>
                <Button type="button" variant="ghost" size="icon" className="absolute right-3 top-3 h-9 w-9" aria-label="Close">
                  <X className="h-4 w-4" />
                </Button>
              </DialogClose>
            </DialogHeader>
            <div className="w-full px-8 pb-6 pt-4 sm:px-10">
              {compareRows.length === 0 ? (
                <p className="text-sm text-muted-foreground">No month-by-month snapshot stored for this event.</p>
              ) : (
                <div className="w-full overflow-hidden rounded-lg border">
                  <div className="bg-muted">
                    <table className="w-full table-fixed border-collapse text-sm">
                      <colgroup>
                        <col className="w-[24%]" />
                        <col className="w-[25.33%]" />
                        <col className="w-[25.33%]" />
                        <col className="w-[25.33%]" />
                      </colgroup>
                      <thead>
                        <tr className="border-b border-border">
                          <th className="py-2.5 pl-4 pr-2 text-left font-medium">Duration</th>
                          <th className="px-2 py-2.5 text-right font-medium">Old price</th>
                          <th className="px-2 py-2.5 text-right font-medium">New price</th>
                          <th className="py-2.5 pl-2 pr-4 text-right font-medium">Difference</th>
                        </tr>
                      </thead>
                    </table>
                  </div>
                  <div className="max-h-[min(52vh,calc(85vh-11rem))] overflow-y-auto overscroll-y-contain bg-background">
                    <table className="w-full table-fixed border-collapse text-sm">
                      <colgroup>
                        <col className="w-[24%]" />
                        <col className="w-[25.33%]" />
                        <col className="w-[25.33%]" />
                        <col className="w-[25.33%]" />
                      </colgroup>
                      <tbody>
                        {compareRows.map(({ mo, oldP, newP, diff }) => (
                          <tr key={mo} className="border-b border-border last:border-0">
                            <td className="py-1.5 pl-4 pr-2 font-medium">
                              {mo} month{mo > 1 ? 's' : ''}
                            </td>
                            <td className={`px-2 py-1.5 text-right font-mono tabular-nums ${oldP != null ? '' : 'text-muted-foreground'}`}>
                              {oldP != null ? fmtGbp(Number(oldP)) : '—'}
                            </td>
                            <td className={`px-2 py-1.5 text-right font-mono tabular-nums ${newP != null ? '' : 'text-muted-foreground'}`}>
                              {newP != null ? fmtGbp(Number(newP)) : '—'}
                            </td>
                            <td
                              className={`py-1.5 pl-2 pr-4 text-right font-mono tabular-nums ${
                                diff == null ? 'text-muted-foreground' : diff > 0 ? 'text-red-500' : diff < 0 ? 'text-green-600' : ''
                              }`}
                            >
                              {diff != null ? `${diff > 0 ? '+' : ''}${fmtGbp(diff)}` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const AssetDetails: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<AssetTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  type Role = { key: string; name: string; description: string };
  type User = { id: string; email: string; first_name: string; last_name: string; roles: Role[] };
  const [user, setUser] = useState<User | null>(null);
  const [uploading, setUploading] = useState(false);
  const [hiresUrls, setHiresUrls] = useState<Record<string, string>>({});
  const [webUrls, setWebUrls] = useState<Record<string, string>>({});
  const [tab, setTab] = useState<
    'overview' | 'pricing' | 'media' | 'instances' | 'performance' | 'revenue' | 'rentals'
  >('overview');
  const webSectionRef = useRef<HTMLDivElement | null>(null);
  const hiresSectionRef = useRef<HTMLDivElement | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const loadAssetTemplate = useCallback(async () => {
    if (!templateId) return;
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/asset-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to load asset template');

      const data = await response.json();
      setTemplate(data.success ? data.data : data);
    } catch (err) {
      console.error('Error loading asset template:', err);
      setError(err instanceof Error ? err.message : 'Failed to load asset template');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    loadAssetTemplate();
    try {
      const userData = localStorage.getItem('user');
      if (userData) setUser(JSON.parse(userData));
    } catch (err) {
      console.error('Error reading user data:', err);
    }
  }, [loadAssetTemplate]);

  const canEdit = user?.roles?.some(r => ['admin', 'supervisor'].includes(r.key)) ?? false;
  const isAdmin = user?.roles?.some(r => r.key === 'admin') ?? false;
  const [supercyclePushing, setSupercyclePushing] = useState(false);
  const [supercycleImporting, setSupercycleImporting] = useState(false);
  const [supercycleInventoryPushing, setSupercycleInventoryPushing] = useState(false);

  const webImages = useMemo(() => (template?.images || []).filter((i: any) => i.variant !== 'hires'), [template]);
  const hiresImages = useMemo(() => (template?.images || []).filter((i: any) => i.variant === 'hires'), [template]);
  const webGridImages = useMemo(() => webImages.map((img: any) => ({ ...img, url: img.url || (img.id ? webUrls[img.id] : undefined) })), [webImages, webUrls]);

  const calendarTierByMonth = useMemo(
    () => buildCalendarTierByMonthMap((template as any)?.rental_pricing_tiers ?? []),
    [template],
  );

  // Prefetch signed URLs for hires previews
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      for (const img of hiresImages) {
        if (!img?.id || hiresUrls[img.id]) continue;
        try {
          const r = await fetch(`/api/asset-templates/images/${img.id}/hires-url`);
          if (!r.ok) continue;
          const d = await r.json();
          const url = d?.data?.url as string | undefined;
          if (!url) continue;
          if (!cancelled) setHiresUrls(prev => ({ ...prev, [img.id]: url }));
        } catch {}
      }
    };
    load();
    return () => { cancelled = true; };
  }, [hiresImages, template?.id]);

  // Prefetch signed URLs for web images missing direct url (staging/private bucket)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      for (const img of webImages) {
        if (!img?.id || img.url || webUrls[img.id]) continue;
        try {
          const r = await fetch(`/api/asset-templates/images/${img.id}/hires-url`);
          if (!r.ok) continue;
          const d = await r.json();
          const url = d?.data?.url as string | undefined;
          if (!url) continue;
          if (!cancelled) setWebUrls(prev => ({ ...prev, [img.id as string]: url }));
        } catch {}
      }
    };
    load();
    return () => { cancelled = true; };
  }, [webImages, template?.id]);

  const getConditionBadge = (condition: string) => {
    const variants: Record<string, string> = {
      new: 'default',
      pre_loved: 'secondary',
      damaged: 'destructive',
      missing: 'warning',
    };
    return variants[condition] || 'secondary';
  };

  const formatSpecifications = (specs: Record<string, any> | null | undefined) => {
    if (!specs || typeof specs !== 'object') return [];
    const dims = parseProductDimensions(specs);
    const rows = Object.entries(specs)
      .filter(([key]) => key !== 'rental_pricing' && key !== 'total_inventory_quantity' && key !== 'dimensions')
      .map(([key, value]) => ({
        key: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)
      }));
    if (dims) rows.unshift({ key: 'Dimensions', value: formatProductDimensions(dims) });
    return rows;
  };

  const formatRentalPricing = (specs: Record<string, any> | null | undefined) => {
    if (!specs?.rental_pricing || !Array.isArray(specs.rental_pricing)) return [];
    return specs.rental_pricing.map((item: any) => ({
      rental_length: item.rental_length || 'N/A',
      price: item.price || 0,
      inventory: item.inventory_quantity || 0,
    }));
  };

  const uploadVariant = async (variant: 'web' | 'hires', files: FileList) => {
    if (!template?.id || !files || files.length === 0) return;
    try {
      setUploading(true);
      setTab('media');
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const form = new FormData();
        form.append('variant', variant);
        form.append('file', f);
        const r = await fetch(`/api/asset-templates/${template.id}/images/upload`, {
          method: 'POST', body: form
        });
        if (!r.ok) throw new Error('Upload failed');
      }
      await loadAssetTemplate();
      setTimeout(() => {
        if (variant === 'hires') hiresSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else webSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    } catch (e) {
      console.error(e);
      window.alert('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="assets" />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center mb-6">
            <Button variant="ghost" size="sm" onClick={() => navigate('/assets')} className="mr-4">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Products
            </Button>
          </div>
          <div className="text-center py-8">Loading asset details...</div>
        </main>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="assets" />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center mb-6">
            <Button variant="ghost" size="sm" onClick={() => navigate('/assets')} className="mr-4">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Products
            </Button>
          </div>
          <div className="text-center py-8 text-red-600">{error || 'Asset template not found'}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="assets" />
      <main className="container mx-auto px-4 py-8">
        {/* Page Title + Actions */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <button
              onClick={() => navigate('/assets')}
              className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-3"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Products
            </button>
            <h1 className="text-3xl font-heading font-bold text-foreground leading-tight">
              {template.name}
            </h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge variant="outline" className="font-mono">{template.template_code}</Badge>
              {template.category?.name && (
                <Badge variant="secondary">{template.category.name}</Badge>
              )}
              <span className="text-sm text-muted-foreground">Product</span>
              {(template as any).shopify_admin_url && (
                <a
                  href={(template as any).shopify_admin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  View in Shopify
                </a>
              )}
            </div>
          </div>
          {canEdit && (
            <Button onClick={() => setEditOpen(true)}>
              <Edit className="h-4 w-4 mr-2" /> Edit Product
            </Button>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={(v) => setTab(v as any)} className="w-full">
          <TabsList className="grid w-full grid-cols-2 gap-1 sm:grid-cols-4 lg:grid-cols-7 h-auto p-1">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Info className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="pricing" className="flex items-center gap-2">
              <Percent className="h-4 w-4" />
              Pricing
            </TabsTrigger>
            <TabsTrigger value="media" className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Media
            </TabsTrigger>
            <TabsTrigger value="instances" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Units
            </TabsTrigger>
            <TabsTrigger value="performance" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Performance
            </TabsTrigger>
          <TabsTrigger value="revenue" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Revenue
            </TabsTrigger>
            <TabsTrigger value="rentals" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              History
            </TabsTrigger>
          </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Hero Section - Image + Quick Info */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Image Carousel - 60% width */}
            {webImages.length > 0 && (
              <Card className="lg:col-span-3">
                <CardContent className="p-6">
                  <Carousel
                    className="w-full"
                    plugins={[Autoplay({ delay: 3000, stopOnInteraction: true })]}
                  >
                    <CarouselContent>
                      {webImages.map((img: any) => (
                        <CarouselItem key={img.id || img.url}>
                          <div className="p-0">
                            <img
                              src={img.url || ''}
                              alt={template.name}
                              className="w-full max-h-[500px] object-contain bg-white rounded border"
                            />
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    <CarouselPrevious />
                    <CarouselNext />
                  </Carousel>
                </CardContent>
              </Card>
            )}

            {/* Quick Info Card - 40% width */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-2xl">{template.name}</CardTitle>
                <CardDescription className="font-mono">{template.template_code}</CardDescription>
                {(template as any).cost_price != null && Number((template as any).cost_price) > 0 && (
                  <div className="text-lg font-semibold text-muted-foreground mt-2">
                    £{Number((template as any).cost_price).toFixed(2)}
                    <span className="text-sm font-normal ml-2">Cost (Trade)</span>
                  </div>
                )}
                {(template as any).cost_price != null && Number((template as any).cost_price) > 0 && (template as any).supplier?.markup_percentage > 0 && (
                  <div className="text-3xl font-bold text-primary">
                    £{(Number((template as any).cost_price) * (1 + Number((template as any).supplier.markup_percentage) / 100)).toFixed(2)}
                    <span className="text-sm font-normal text-muted-foreground ml-2">RRP (+{(template as any).supplier.markup_percentage}%)</span>
                  </div>
                )}
                {template.base_price != null && !((template as any).supplier?.markup_percentage > 0) && (
                  <div className="text-xl font-semibold text-muted-foreground">
                    £{Number(template.base_price).toFixed(2)}
                    <span className="text-sm font-normal ml-2">Shopify Price</span>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {(template as any).supplier && (
                  <div>
                    <div className="text-xs uppercase tracking-wide font-bold text-muted-foreground">Supplier</div>
                    <a href={`/suppliers/${(template as any).supplier.id}`} className="text-base font-medium text-primary hover:underline mt-1 inline-block">
                      {(template as any).supplier.name}
                    </a>
                  </div>
                )}
                <div>
                  <div className="text-xs uppercase tracking-wide font-bold text-muted-foreground">Category</div>
                  <div className="text-base font-medium text-foreground mt-1">{template.category?.name || 'No category'}</div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-wide font-bold text-muted-foreground">Supplier Stock</div>
                    <div className="text-base font-medium text-foreground mt-1">
                      {getSupplierStock(template)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide font-bold text-muted-foreground">Warehouse Stock</div>
                    <div className="text-base font-medium text-foreground mt-1">
                      {template.warehouse_stock == null ? '—' : template.warehouse_stock}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap">
                  <div>
                    <div className="text-xs uppercase tracking-wide font-bold text-muted-foreground mb-1">Status</div>
                    <Badge variant={template.is_active ? 'default' : 'secondary'}>
                      {template.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide font-bold text-muted-foreground mb-1">Condition</div>
                    <Badge variant={getConditionBadge(template.default_condition) as any}>
                      {template.default_condition.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Badge>
                  </div>
                </div>

                {template.created_by_user && (
                  <div>
                    <div className="text-xs uppercase tracking-wide font-bold text-muted-foreground">Created By</div>
                    <div className="text-sm font-medium text-foreground mt-1">
                      {template.created_by_user.first_name} {template.created_by_user.last_name}
                    </div>
                    <p className="text-xs text-muted-foreground">{template.created_by_user.email}</p>
                  </div>
                )}

                <div>
                  <div className="text-xs uppercase tracking-wide font-bold text-muted-foreground">Created At</div>
                  <div className="text-sm text-foreground mt-1">
                    {new Date(template.created_at).toLocaleDateString()} at{' '}
                    {new Date(template.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
            {template.description && (
              <Card className="min-w-0">
                <CardHeader>
                  <CardTitle>Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <div
                    className="prose prose-sm max-w-none text-foreground"
                    dangerouslySetInnerHTML={{ __html: template.description }}
                  />
                </CardContent>
              </Card>
            )}

            <Card className={`min-w-0 ${template.description ? '' : 'lg:col-span-2'}`}>
              <CardHeader>
                <CardTitle>Specifications</CardTitle>
              </CardHeader>
              <CardContent>
                {formatSpecifications(template.specifications).length > 0 ? (
                  <div className="space-y-3">
                    {formatSpecifications(template.specifications).map(({ key, value }) => (
                      <div key={key} className="flex justify-between gap-4 py-2 border-b last:border-0">
                        <span className="text-sm font-medium text-muted-foreground shrink-0">{key}</span>
                        <span className="text-sm font-medium text-foreground text-right break-words">{value}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">No specifications defined</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pricing" className="space-y-6">
          {((template as any).rental_pricing_tiers?.length > 0 || formatRentalPricing(template.specifications).length > 0) ? (
            <Card>
              <CardHeader>
                <CardTitle>Rental pricing calendar</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(template as any).rental_pricing_tiers?.length > 0 && (
                  <>
                    {(template as any).rental_pricing_tiers.filter((t: any) => t.pricing_type === 'calendar').length > 0 && (
                      <div className="overflow-x-auto rounded-lg border">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted">
                              <th className="sticky left-0 z-10 px-3 py-2 text-left font-medium">Duration</th>
                              <th className="px-3 py-2 text-right font-medium">Current Margin</th>
                              <th className="px-3 py-2 text-right font-medium">Monthly Price</th>
                              <th className="px-3 py-2 text-right font-medium">Per Day</th>
                              <th className="px-3 py-2 text-right font-medium">Total Revenue</th>
                              <th className="px-3 py-2 text-right font-medium">Cost Gap</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from({ length: CALENDAR_PLANNER_MONTHS }, (_, i) => i + 1).map(mo => {
                              const tier = calendarTierByMonth.get(mo);
                              const cost = Number((template as any).cost_price) || 0;
                              const total = tier != null ? Number(tier.total_price) : NaN;
                              const hasTier = tier != null && Number.isFinite(total);
                              const monthly = hasTier ? total / mo : NaN;
                              const marginPct = hasTier && cost > 0 ? (total / cost) * 100 : null;
                              const profit = hasTier && cost > 0 ? total - cost : null;
                              return (
                                <tr key={mo} className="border-b last:border-0">
                                  <td className="sticky left-0 py-1.5 pl-3 pr-2 font-medium">
                                    {mo} month{mo > 1 ? 's' : ''}
                                  </td>
                                  <td
                                    className={`px-3 py-1.5 text-right font-mono tabular-nums ${
                                      marginPct != null ? '' : 'text-muted-foreground'
                                    }`}
                                  >
                                    {marginPct != null ? `${marginPct.toFixed(1)}%` : '—'}
                                  </td>
                                  <td className={`px-3 py-1.5 text-right font-mono tabular-nums ${hasTier ? '' : 'text-muted-foreground'}`}>
                                    {hasTier ? fmtGbp(monthly) : '—'}
                                  </td>
                                  <td className="px-3 py-1.5 text-right font-mono text-muted-foreground tabular-nums">
                                    {hasTier ? fmtGbp(Number(tier.price_per_day)) : '—'}
                                  </td>
                                  <td className={`px-3 py-1.5 text-right font-mono tabular-nums ${hasTier ? '' : 'text-muted-foreground'}`}>
                                    {hasTier ? fmtGbp(total) : '—'}
                                  </td>
                                  <td
                                    className={`px-3 py-1.5 text-right font-mono tabular-nums ${
                                      profit == null ? 'text-muted-foreground' : profit >= 0 ? 'text-green-600' : 'text-red-500'
                                    }`}
                                  >
                                    {profit != null ? `${profit >= 0 ? '+' : ''}${fmtGbp(profit)}` : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {(template as any).rental_pricing_tiers.filter((t: any) => t.pricing_type === 'subscription').length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Subscription plans</h4>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-muted">
                              <tr>
                                <th className="px-4 py-2 text-left font-medium">Plan</th>
                                <th className="px-4 py-2 text-right font-medium">Initial</th>
                                <th className="px-4 py-2 text-right font-medium">Monthly</th>
                                <th className="px-4 py-2 text-right font-medium">Per day</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(template as any).rental_pricing_tiers
                                .filter((t: any) => t.pricing_type === 'subscription')
                                .map((tier: any, idx: number) => (
                                  <tr key={tier.id || idx} className="border-t">
                                    <td className="px-4 py-2">{tier.name}</td>
                                    <td className="px-4 py-2 text-right">£{Number(tier.initial_price || 0).toFixed(2)}</td>
                                    <td className="px-4 py-2 text-right font-semibold">£{Number(tier.recurring_price).toFixed(2)}</td>
                                    <td className="px-4 py-2 text-right text-muted-foreground">£{Number(tier.price_per_day).toFixed(2)}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {!(template as any).rental_pricing_tiers?.length && formatRentalPricing(template.specifications).length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium">Rental length</th>
                          <th className="px-4 py-3 text-right font-medium">Price</th>
                          <th className="px-4 py-3 text-right font-medium">Inventory</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formatRentalPricing(template.specifications).map((item, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-4 py-3">{item.rental_length}</td>
                            <td className="px-4 py-3 text-right font-semibold">£{Number(item.price).toFixed(2)}</td>
                            <td className="px-4 py-3 text-right text-muted-foreground">{item.inventory} units</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Rental pricing calendar</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">No rental pricing tiers or legacy rental rows for this product.</p>
              </CardContent>
            </Card>
          )}

          {isAdmin && (template as any).cost_price && Number((template as any).cost_price) > 0 && (template as any).supplier && (
            <BreakEvenSummaryCard templateId={template.id} supplierId={(template as any).supplier?.id} />
          )}
          {(template as any).supplier?.id && (
            <TemplatePricingPublishHistoryCard templateId={template.id} />
          )}

          {isAdmin && (template as any).shopify_product_id && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Supercycle</CardTitle>
                <CardDescription className="space-y-1">
                  <p>
                    Subscription tiers and inventory sync are in <strong>Settings → Supercycle sync</strong>.
                  </p>
                  <button
                    type="button"
                    className="text-primary text-sm underline"
                    onClick={() => navigate('/settings/supercycle-subscription-push')}
                  >
                    Open Supercycle page
                  </button>
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={supercyclePushing}
                  onClick={async () => {
                    if (!templateId) return;
                    setSupercyclePushing(true);
                    try {
                      const t = localStorage.getItem('token');
                      const r = await fetch(`/api/asset-templates/${templateId}/supercycle/subscription-prices/push`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${t}` },
                      });
                      const d = await r.json();
                      if (!r.ok) throw new Error(d.message || 'Push failed');
                      const row = d.data;
                      if (row?.status === 'ok') toast.success('Pushed subscription prices to Supercycle');
                      else toast.message(row?.message || row?.status || 'Done');
                      await loadAssetTemplate();
                    } catch (e) {
                      toast.error(e instanceof Error ? e.message : 'Push failed');
                    } finally {
                      setSupercyclePushing(false);
                    }
                  }}
                >
                  {supercyclePushing ? 'Pushing…' : 'Push subscription prices to Supercycle'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={supercycleImporting}
                  onClick={async () => {
                    if (!templateId) return;
                    setSupercycleImporting(true);
                    try {
                      const t = localStorage.getItem('token');
                      const r = await fetch(
                        `/api/asset-templates/${templateId}/subscription-pricing-tiers/import-from-shopify`,
                        { method: 'POST', headers: { Authorization: `Bearer ${t}` } },
                      );
                      const d = await r.json();
                      if (!r.ok) throw new Error(d.message || 'Import failed');
                      toast.success(`Imported ${d.data?.imported ?? 0} subscription tier(s) from Shopify`);
                      await loadAssetTemplate();
                    } catch (e) {
                      toast.error(e instanceof Error ? e.message : 'Import failed');
                    } finally {
                      setSupercycleImporting(false);
                    }
                  }}
                >
                  {supercycleImporting ? 'Importing…' : 'Import from Shopify (optional)'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={supercycleInventoryPushing}
                  onClick={async () => {
                    if (!templateId) return;
                    setSupercycleInventoryPushing(true);
                    try {
                      const t = localStorage.getItem('token');
                      const r = await fetch(`/api/asset-templates/${templateId}/supercycle/inventory/push`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${t}` },
                      });
                      const d = await r.json();
                      if (!r.ok) throw new Error(d.message || 'Inventory sync failed');
                      const row = d.data;
                      if (row?.status === 'ok') toast.success(row.message || 'Inventory synced to Supercycle');
                      else toast.message(row?.message || row?.status || 'Done');
                      await loadAssetTemplate();
                    } catch (e) {
                      toast.error(e instanceof Error ? e.message : 'Inventory sync failed');
                    } finally {
                      setSupercycleInventoryPushing(false);
                    }
                  }}
                >
                  {supercycleInventoryPushing ? 'Syncing…' : 'Sync inventory to Supercycle'}
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Media Tab: Public + Hi-Res with upload */}
        <TabsContent value="media" className="space-y-6">
          <Card ref={webSectionRef}>
            <CardHeader>
              <CardTitle>Public Images (Web)</CardTitle>
              <CardDescription>Shown on product pages</CardDescription>
            </CardHeader>
            <CardContent>
              {canEdit && (
                <div className="mb-3">
                  <input type="file" accept="image/*" multiple disabled={uploading} onChange={async (e) => {
                    if (!e.currentTarget.files) return;
                    await uploadVariant('web', e.currentTarget.files);
                    // Attempt to fetch signed URLs for web images lacking url to populate previews
                    try {
                      const missing = webImages.filter((i: any) => !i.url);
                      for (const img of missing) {
                        const r = await fetch(`/api/asset-templates/images/${img.id}/hires-url`);
                        if (r.ok) {
                          const d = await r.json();
                          if (d?.data?.url) (img as any).url = d.data.url;
                        }
                      }
                    } catch {}
                  }} />
                </div>
              )}
              <ReorderableGrid images={webGridImages as any} templateId={template.id} onReordered={loadAssetTemplate} />
            </CardContent>
          </Card>

          <Card ref={hiresSectionRef}>
            <CardHeader>
              <CardTitle>Hi‑Res Images (Private)</CardTitle>
              <CardDescription>Secure images for design/mood boards</CardDescription>
            </CardHeader>
            <CardContent>
              {canEdit && (
                <div className="mb-3">
                  <input type="file" accept="image/*" multiple disabled={uploading} onChange={(e) => e.currentTarget.files && uploadVariant('hires', e.currentTarget.files)} />
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {hiresImages.map((img: any, idx: number) => (
                  <div key={img.id} className="border rounded overflow-hidden p-2 text-center">
                    {hiresUrls[img.id]
                      ? (
                        <div className="mb-2">
                          <AspectRatioImage
                            src={hiresUrls[img.id]}
                            alt={`Hi-res ${idx + 1}`}
                            size="full"
                          />
                        </div>
                      )
                      : (
                        <button className="h-32 w-full flex items-center justify-center text-xs text-muted-foreground mb-2 underline"
                          onClick={async () => {
                            try {
                              const r = await fetch(`/api/asset-templates/images/${img.id}/hires-url`);
                              if (!r.ok) throw new Error('Failed to get URL');
                              const d = await r.json();
                              const url = d?.data?.url;
                              if (url) setHiresUrls(prev => ({ ...prev, [img.id]: url }));
                            } catch {}
                          }}
                        >Load preview</button>
                      )}
                    <div className="flex items-center justify-center gap-3">
                      <button className="text-sm underline" onClick={async () => {
                        try {
                          let url = hiresUrls[img.id];
                          if (!url) {
                            const r = await fetch(`/api/asset-templates/images/${img.id}/hires-url`);
                            if (!r.ok) throw new Error('Failed to get URL');
                            const d = await r.json();
                            url = d?.data?.url;
                            if (url) setHiresUrls(prev => ({ ...prev, [img.id]: url! }));
                          }
                          if (url) window.open(url, '_blank');
                        } catch (e) { window.alert('Could not open hi-res image'); }
                      }}>Open</button>
                      <button className="text-sm underline" onClick={async () => {
                        try {
                          let url = hiresUrls[img.id];
                          if (!url) {
                            const r = await fetch(`/api/asset-templates/images/${img.id}/hires-url`);
                            if (!r.ok) throw new Error('Failed to get URL');
                            const d = await r.json();
                            url = d?.data?.url;
                          }
                          if (!url) throw new Error('No URL');
                          const resp = await fetch(url);
                          if (!resp.ok) throw new Error('Download failed');
                          const blob = await resp.blob();
                          const blobUrl = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = blobUrl;
                          const name = (img.storage_key?.split('/')?.pop()) || (template.name?.replace(/\s+/g,'-').toLowerCase() + '-' + img.id + '.jpg');
                          a.download = name;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          URL.revokeObjectURL(blobUrl);
                        } catch (e) { window.alert('Could not download'); }
                      }}>Download</button>
                      {canEdit && (
                        <button className="text-sm underline text-red-600" onClick={async () => {
                          if (!window.confirm('Delete this image? This cannot be undone.')) return;
                          try {
                            const token = localStorage.getItem('token');
                            const r = await fetch(`/api/asset-templates/images/${img.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
                            if (!r.ok) throw new Error('Failed to delete');
                            // refresh
                            await loadAssetTemplate();
                          } catch { window.alert('Failed to delete image'); }
                        }}>Delete</button>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">#{idx + 1}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Units Tab */}
        <TabsContent value="instances">
          <AssetInstancesTab templateId={templateId!} template={template} />
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance">
          <AssetPerformanceTab templateId={templateId!} />
        </TabsContent>

        {/* Revenue Tab */}
        <TabsContent value="revenue">
          <AssetRevenueTab templateId={templateId!} />
        </TabsContent>

        {/* Rentals/History Tab */}
        <TabsContent value="rentals">
          <RentalHistory unitId={template.instances?.[0]?.id || ''} />
        </TabsContent>
      </Tabs>
      </main>
      {/* Edit Product Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
            <DialogDescription>Update details for "{template.name}"</DialogDescription>
          </DialogHeader>
          <AssetTemplateForm
            template={template as any}
            onSuccess={async () => { setEditOpen(false); await loadAssetTemplate(); }}
            onCancel={() => setEditOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetDetails;
